import { useMemo } from "react";

import { usePostHog } from "posthog-js/react";

import { useSwrFetch } from "hooks";

import { ShipmentCopyModal } from "./ShipmentCopyModal";
import { createShipmentFormState } from "./utils";
import { ClientAppEvent } from "../../constants";

interface Props {
  shipment: CondensedShipment | undefined;
  onShipmentCopyClose: VoidFunction;
}

export function ShipmentCopyModalContainer(props: Props) {
  const { shipment, onShipmentCopyClose } = props;
  const posthog = usePostHog();

  const { data: routeResponse, error: routeError } =
    useSwrFetch<GetRouteResponse>({
      url: shipment !== undefined ? `/routes/${shipment.info.route}` : null,
      errorMessage: "Failed to copy route information, please re-enter",
      swrOptions: {
        onSuccess: () => {
          posthog?.capture(ClientAppEvent.SHIPMENT_COPY_MODAL_OPENED, {
            source_shipment_id: shipment?.info?.id ?? undefined,
            route_fetch_success: true,
            source_shipment_type: shipment?.info?.type ?? undefined,
            source_shipment_status: shipment?.shipment_status ?? undefined,
          });
        },
      },
    });

  const error =
    shipment === undefined ? "Failed to load shipment information" : routeError;

  // Memoized so shipmentState keeps a stable identity across renders: it seeds
  // CreateShipmentFormContainer's form state (via a prop-sync effect) and backs
  // the route-point React keys (createShipmentFormState mints fresh clientIds on
  // each call), both of which reset on every render if this object churns.
  const shipmentState = useMemo(() => {
    if (shipment === undefined) {
      return undefined;
    }
    return createShipmentFormState(
      shipment as ShipperCondensedShipment,
      routeResponse?.route.route_points ?? [],
    );
  }, [shipment, routeResponse]);

  return (
    <ShipmentCopyModal
      onClose={onShipmentCopyClose}
      shipmentState={shipmentState}
      error={error}
    />
  );
}
